import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Leaf,
  Lightbulb,
  Users,
  ArrowRight,
  CheckCircle,
  Target,
  Droplets,
  Compass,
  Heart,
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Leaf,
      title: 'Durabilité',
      description: 'Nous intégrons systématiquement les principes de gestion durable de l\'eau et d\'adaptation climatique dans nos mandats. Chaque solution est pensée pour assurer performance et pérennité.',
    },
    {
      icon: Lightbulb,
      title: 'Excellence technique',
      description: 'Notre force repose sur une expertise approfondie en hydraulique urbaine, modélisation et conception d\'infrastructures. La précision analytique et la validation scientifique sont au cœur de notre démarche.',
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Nous privilégions une approche collaborative, favorisant l\'échange entre experts, partenaires et clients. La complémentarité des compétences permet d\'atteindre des résultats structurants et durables.',
    },
  ];

  const philosophy = [
    'Une vision globale du territoire',
    'Une maîtrise des cadres réglementaires',
    'Une culture de précision et de responsabilité',
    'Une recherche constante d\'optimisation et d\'innovation',
  ];

  const convictionPoints = [
    'Rigueur technique et analyse approfondie',
    'Innovation et amélioration continue',
    'Responsabilité environnementale',
    'Vision à long terme',
  ];

  const parcoursItems = [
    'La gestion des eaux pluviales et de ruissellement',
    'Le drainage durable des infrastructures routières et ferroviaires',
    'L\'intégration d\'infrastructures vertes',
    'L\'évaluation et la valorisation des actifs municipaux',
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-[#1D1D1B] py-20 lg:py-24">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="text-[#FFD537] font-semibold mb-4 animate-fadeIn">NOTRE ADN</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6 animate-fadeIn">
              L'ingénierie hydraulique au cœur de notre identité
            </h1>
            <p className="text-xl text-white/70 leading-relaxed animate-fadeIn animation-delay-100">
              Chez PEY Consultants, notre ADN repose sur une conviction claire : la résilience des milieux urbains commence par une compréhension approfondie du comportement de l'eau.
            </p>
          </div>
        </div>
      </section>

      {/* Qui nous sommes */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=800&q=80"
                alt="Infrastructure hydraulique"
                className="rounded-xl shadow-2xl w-full object-cover h-[400px]"
              />
            </div>
            <div>
              <p className="text-[#F9B438] font-semibold mb-2">QUI NOUS SOMMES</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-6">
                Une expertise structurée au service de décisions éclairées
              </h2>
              <p className="text-neutral-600 text-lg leading-relaxed mb-6">
                PEY Consultants Inc. est une firme québécoise de génie-conseil qui accompagne les organisations publiques et privées dans la réalisation de projets structurants.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Au-delà de la conception technique, nous intervenons comme partenaire stratégique, en apportant une compréhension approfondie des enjeux territoriaux, réglementaires et climatiques qui influencent la performance des infrastructures.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                Notre approche repose sur l'analyse rigoureuse des données, une méthodologie éprouvée et une capacité à traduire la complexité technique en solutions concrètes, durables et adaptées aux réalités locales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre philosophie */}
      <section className="section-padding bg-neutral-50">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F9B438] font-semibold mb-2">NOTRE PHILOSOPHIE</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-6">
                Une infrastructure bien conçue est une infrastructure pensée à long terme
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Chaque mandat est abordé avec rigueur et vision. Notre équipe multidisciplinaire conjugue expertise technique, rigueur scientifique et compréhension des besoins municipaux.
              </p>
              <ul className="space-y-4">
                {philosophy.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)' }}
                    >
                      <CheckCircle className="w-4 h-4 text-[#F9B438]" />
                    </div>
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="p-8">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                >
                  <Users className="w-8 h-8 text-[#F9B438]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1D1D1B] mb-4">Une équipe engagée</h3>
                <p className="text-neutral-600 leading-relaxed">
                  Notre force repose sur une équipe multidisciplinaire qui conjugue expertise technique, rigueur scientifique et compréhension des besoins municipaux. Nous privilégions la collaboration, la transparence et la qualité des livrables, afin d'assurer à nos clients une prise de décision éclairée et sécurisée.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Notre approche */}
      <section 
        className="py-16"
        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.1) 0%, rgba(249,180,56,0.1) 100%)' }}
      >
        <div className="container-app">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#F9B438] font-semibold mb-2 text-center">NOTRE APPROCHE</p>
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-8 text-center">
              Une approche fondée sur la rigueur et l'innovation
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'Modélisation hydraulique avancée',
                'Planification stratégique à l\'échelle des bassins versants',
                'Intégration d\'infrastructures vertes et de solutions fondées sur la nature',
                'Utilisation de relevés terrain et de technologies de captation par drone',
                'Analyse scientifique structurée et documentée',
                'Solutions fiables, évolutives et adaptées',
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                  >
                    <CheckCircle className="w-4 h-4 text-[#1D1D1B]" />
                  </div>
                  <span className="text-neutral-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section 
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
      >
        <div className="container-app">
          <div className="max-w-4xl mx-auto text-center">
            <Target className="w-16 h-16 text-[#1D1D1B] mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1D1D1B] mb-6">
              Notre mission
            </h2>
            <p className="text-[#1D1D1B]/90 text-xl leading-relaxed mb-6">
              Contribuer à la résilience des collectivités en concevant des solutions d'ingénierie performantes, durables et responsables.
            </p>
            <p className="text-[#1D1D1B]/80 leading-relaxed">
              Chez PEY Consultants, nous développons des infrastructures adaptées aux réalités urbaines et climatiques actuelles, en assurant une gestion rigoureuse de l'eau et une optimisation durable des systèmes municipaux. Nous mettons notre expertise au service d'un aménagement réfléchi, sécuritaire et respectueux des ressources, afin de bâtir des milieux de vie durables pour les générations futures.
            </p>
          </div>
        </div>
      </section>

      {/* Conviction Section */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F9B438] font-semibold mb-2">NOTRE CONVICTION</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-6">
                Chaque projet représente une opportunité d'améliorer durablement le territoire
              </h2>
              <p className="text-neutral-600 text-lg leading-relaxed mb-6">
                Nous croyons qu'une infrastructure bien planifiée et scientifiquement validée permet de réduire les risques, d'optimiser les investissements publics et de renforcer la sécurité des collectivités.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Notre approche combine :
              </p>
              <ul className="space-y-4 mb-8">
                {convictionPoints.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)' }}
                    >
                      <CheckCircle className="w-4 h-4 text-[#F9B438]" />
                    </div>
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-neutral-600 leading-relaxed">
                Nous accompagnons nos clients vers des décisions éclairées et des solutions résilientes, adaptées aux défis croissants des milieux urbains.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=800&q=80"
                alt="Projet d'infrastructure"
                className="rounded-xl shadow-2xl w-full object-cover h-[450px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Quote */}
      <section className="py-16 bg-[#1D1D1B]">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-12 h-12 text-[#FFD537] mx-auto mb-6" />
            <blockquote className="text-xl sm:text-2xl text-white font-medium italic mb-6 leading-relaxed">
              « Chez PEY Consultants, nous sommes convaincus qu'un environnement urbain résilient repose sur une gestion responsable, durable et innovante de l'eau. »
            </blockquote>
            <p className="text-[#FFD537] font-semibold">— NOTRE ENGAGEMENT</p>
            <p className="text-white/60 mt-4">
              Cette vision oriente chacune de nos décisions techniques et stratégiques.
            </p>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80"
                alt="Ville moderne"
                className="rounded-xl shadow-2xl w-full object-cover h-[400px]"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#F9B438] font-semibold mb-2">NOTRE PARCOURS</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-6">
                Depuis 2020
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Fondée au Québec, PEY Consultants s'est spécialisée dans l'analyse et l'optimisation des systèmes hydrauliques urbains. Nous accompagnons les municipalités, institutions et promoteurs dans la planification et la conception de projets liés notamment à :
              </p>
              <ul className="space-y-3 mb-6">
                {parcoursItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                    />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-neutral-600 leading-relaxed">
                Notre croissance repose sur une combinaison de <strong>spécialisation technique</strong>, d'<strong>agilité organisationnelle</strong> et d'un <strong>engagement constant</strong> envers la qualité des livrables.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="text-center mb-12">
            <p className="text-[#F9B438] font-semibold mb-2">CE QUI NOUS GUIDE</p>
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">
              Nos valeurs
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Les principes fondamentaux qui définissent notre approche et notre culture d'entreprise.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index} 
                className="group animate-fadeIn"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                  >
                    <value.icon className="w-8 h-8 text-[#F9B438]" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-3 text-[#1D1D1B]">{value.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Penser à long terme */}
      <section 
        className="py-16"
        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.1) 0%, rgba(249,180,56,0.1) 100%)' }}
      >
        <div className="container-app">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-6">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
              >
                <Compass className="w-8 h-8 text-[#1D1D1B]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-[#1D1D1B] mb-4">
                  Penser à long terme
                </h2>
                <p className="text-neutral-600 leading-relaxed mb-4">
                  Nous ne concevons pas uniquement des ouvrages. Nous structurons des systèmes durables capables de répondre aux pressions croissantes liées aux changements climatiques, à l'urbanisation et aux exigences réglementaires.
                </p>
                <p className="text-neutral-600 leading-relaxed">
                  Chaque intervention est guidée par une vision à long terme visant à optimiser la performance des réseaux tout en réduisant les risques pour les collectivités.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1D1D1B] py-16">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Travaillons ensemble
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Discutons de votre prochain projet d'infrastructure durable.
          </p>
          <Link to="/contact">
            <Button size="lg">
              Nous contacter
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
